import { useRef, useState, useEffect, useMemo } from 'react'
import type { Therapist } from '../../stores/chatStore'
import { useChatStore } from '../../stores/chatStore'
import { useAuthStore } from '../../stores/authStore'

interface VideoPlayerProps {
  src: string
  therapist?: Therapist | null
  autoPlay?: boolean
  isUser?: boolean
  messageId?: number // Used to track if already auto-played
}

// Track which messages have been auto-played (persisted in localStorage)
const PLAYED_STORAGE_KEY = 'jt_played_messages'

// Module-level cache to avoid re-parsing localStorage every call
let _playedCache: Set<string> | null = null

function getPlayedMessages(): Set<string> {
  if (_playedCache) return _playedCache
  try {
    const stored = localStorage.getItem(PLAYED_STORAGE_KEY)
    _playedCache = stored ? new Set(JSON.parse(stored)) : new Set()
    return _playedCache
  } catch {
    _playedCache = new Set()
    return _playedCache
  }
}

function markAsPlayed(key: string): void {
  try {
    const played = getPlayedMessages()
    played.add(key)
    // Keep only last 500 entries to avoid localStorage bloat
    const arr = Array.from(played)
    if (arr.length > 500) {
      arr.splice(0, arr.length - 500)
    }
    localStorage.setItem(PLAYED_STORAGE_KEY, JSON.stringify(arr))
  } catch {
    // Ignore localStorage errors
  }
}

function hasBeenPlayed(key: string): boolean {
  return getPlayedMessages().has(key)
}

export default function VideoPlayer({ src, therapist, autoPlay = true, isUser = false, messageId }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const isPlayingRef = useRef(false)
  const [isQueued, setIsQueued] = useState(false)
  const [videoReady, setVideoReady] = useState(false)
  const playbackRate = useChatStore((s) => s.playbackRate)
  const currentlyPlayingId = useChatStore((s) => s.currentlyPlayingId)
  const enqueuePlayback = useChatStore((s) => s.enqueuePlayback)
  const startPlayback = useChatStore((s) => s.startPlayback)
  const finishPlayback = useChatStore((s) => s.finishPlayback)
  const canPlay = useChatStore((s) => s.canPlay)
  const accessToken = useAuthStore((state) => state.accessToken)

  // Add token to video URL for authentication (video elements don't send Authorization headers)
  const videoSrc = useMemo(() => {
    if (!src || src.startsWith('blob:')) return src
    const url = new URL(src, window.location.origin)
    if (accessToken) {
      url.searchParams.set('token', accessToken)
    }
    return url.toString()
  }, [src, accessToken])

  // Apply playback rate when it changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate
    }
  }, [playbackRate])

  // Keep ref in sync for cleanup (closures capture stale state)
  useEffect(() => { isPlayingRef.current = isPlaying }, [isPlaying])

  // Release playback queue on unmount (prevents stuck queue on layout transition)
  useEffect(() => {
    return () => {
      if (isPlayingRef.current) finishPlayback()
    }
  }, [finishPlayback])

  // Track video readiness separately — stable listener, won't be removed by queue state changes
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (video.readyState >= 3) {
      setVideoReady(true)
    } else {
      const handleCanPlay = () => setVideoReady(true)
      video.addEventListener('canplay', handleCanPlay, { once: true })
      return () => video.removeEventListener('canplay', handleCanPlay)
    }
  }, [videoSrc])

  // Queue for autoplay on mount (only once per message, persisted)
  useEffect(() => {
    // Create unique key for this message/src combo
    const playKey = messageId ? `video-${messageId}` : `video-${src}`

    if (autoPlay && videoRef.current && messageId && !hasBeenPlayed(playKey)) {
      markAsPlayed(playKey)
      enqueuePlayback(messageId)
      setIsQueued(true)
    }
  }, [videoSrc, autoPlay, messageId, src, enqueuePlayback])

  // Check if it's our turn to play from the queue
  useEffect(() => {
    if (!isQueued || !messageId || !videoRef.current || !videoReady) return
    if (isPlaying) return // Already playing

    // Check if we can play now
    if (canPlay(messageId)) {
      const video = videoRef.current
      startPlayback(messageId)
      setIsQueued(false)
      video.playbackRate = playbackRate
      video.play().catch(() => {
        // Browser may block autoplay
        finishPlayback() // Release queue on error
      })
    }
  }, [isQueued, messageId, currentlyPlayingId, canPlay, startPlayback, finishPlayback, isPlaying, playbackRate, videoReady])

  const handleVideoClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        // Manual play - mark as playing in queue
        if (messageId) {
          startPlayback(messageId)
        }
        videoRef.current.play()
      }
    }
  }

  // Handle video ended - notify queue
  const handleVideoEnded = () => {
    setIsPlaying(false)
    finishPlayback()
  }

  // Border colors - user vs therapist
  const borderColors: Record<string, string> = {
    wendy: 'border-wendy',
    artem: 'border-artem',
    finch: 'border-finch',
    una: 'border-una',
    user: 'border-primary-500', // User video circle color
  }

  const borderColor = isUser
    ? borderColors.user
    : therapist?.type
      ? borderColors[therapist.type] || 'border-primary-500'
      : 'border-primary-500'

  return (
    <>
      {/* Circular video - plays inline with sound */}
      <div className="relative group">
        <button
          onClick={(e) => {
            e.preventDefault()
            handleVideoClick()
          }}
          className="relative"
        >
          <div
            className={`video-circle w-32 h-32 border-4 ${borderColor}
                       overflow-hidden shadow-lg transition-all duration-300
                       hover:scale-105 active:scale-95
                       ${isPlaying ? 'scale-105 shadow-xl ring-4 ring-primary-400/40' : ''}`}
          >
            <video
              ref={videoRef}
              src={videoSrc}
              className="w-full h-full object-cover"
              playsInline
              preload="auto"
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={handleVideoEnded}
            />
          </div>

          {/* Play overlay - only show when paused */}
          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-full">
              <svg
                className="w-10 h-10 text-white drop-shadow-lg"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          )}
        </button>

      </div>
    </>
  )
}
